import { createElement, Fragment, FunctionComponent, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

const modalsController: {
    modals: {
        key: string;
        Component: FunctionComponent<ModalProps>;
        initialProps: Omit<ModalProps, "onClose">;
    }[];
    listeners: (() => void)[];
    addListener(listener: () => void);
    removeListener(listener: () => void);
    dispatch();
    close(key: string);
    display<Props extends ModalProps>(
        key: string,
        Component: FunctionComponent<Props>,
        initialProps: Omit<Props, "onClose">
    );
} = {
    modals: [],
    listeners: [],
    addListener(listener: () => void) {
        modalsController.listeners.push(listener);
    },
    removeListener(listener: () => void) {
        const index = modalsController.listeners.indexOf(listener);

        if (index >= 0) {
            modalsController.listeners.splice(index, 1);
        }
    },
    dispatch() {
        for (const listener of modalsController.listeners) {
            listener?.();
        }
    },
    close(key: string) {
        modalsController.modals = modalsController.modals.filter((modal) => modal.key !== key);
        modalsController.dispatch();
    },
    display<Props extends ModalProps>(
        key: string,
        Component: FunctionComponent<Props>,
        initialProps: Omit<Props, "onClose">
    ) {
        modalsController.modals.push({ key, Component, initialProps });
        modalsController.dispatch();
    },
};

export const withModals = <Props>(Component: FunctionComponent<Props>) => {
    return (props: Props) => {
        const location = useLocation();

        const [, setModals] = useState([]);

        const handleChange = () => setModals(modalsController.modals.map((modal) => modal?.key));

        useEffect(() => {
            modalsController.addListener(handleChange);
            return () => modalsController.removeListener(handleChange);
        }, []);

        useEffect(() => {
            const modals = modalsController.modals.map((modal) => modal?.key);

            for (const key of modals.reverse()) {
                modalsController.close(key);
            }
        }, [location?.pathname]);

        return createElement(Fragment, {}, [
            createElement(Component, { key: "component", ...props }),
            createElement(
                Fragment,
                { key: "modals" },
                modalsController.modals.map(({ Component, key, initialProps }, index) =>
                    createElement(Component, {
                        key: `${index}${key}`,
                        onClose: () => modalsController.close(key),
                        ...initialProps,
                    })
                )
            ),
        ]);
    };
};

export interface ModalProps {
    onClose?();
}

export const createModal = <Props extends object>(Component: FunctionComponent<Props & ModalProps>) => {
    return {
        display(props: Omit<Props, "onClose">) {
            const key = Math.random().toString(36).substring(2);
            modalsController.display(key, Component, props);
        },
    };
};
